import React, { useEffect, useRef, useState } from "react";
import settings from "@content/settings.json";
import batches from "@content/batches.json";

const STORAGE_KEY = "g2p_exit_offer_seen";
const SHOW_AGAIN_AFTER_DAYS = 7;

const shouldShow = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return true;
        const ts = parseInt(raw, 10);
        if (!ts) return true;
        const days = (Date.now() - ts) / (1000 * 60 * 60 * 24);
        return days > SHOW_AGAIN_AFTER_DAYS;
    } catch {
        return true;
    }
};

const markShown = () => {
    try {
        localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
        /* ignore */
    }
};

const TRUST_POINTS = [
    "7 Days Live Practical Training",
    "Enterprise Networking Labs",
    "Day 2 Money-Back Guarantee",
    "Only ₹799",
];

const ExitIntent = () => {
    const [open, setOpen] = useState(false);
    const armedRef = useRef(true);
    const timerRef = useRef(null);
    const scrollFiredRef = useRef(false);

    const trigger = () => {
        if (!armedRef.current) return;
        if (!shouldShow()) return;
        armedRef.current = false;
        setOpen(true);
        markShown();
    };

    useEffect(() => {
        if (!shouldShow()) return;

        const isTouch = window.matchMedia("(hover: none)").matches;

        // Desktop: mouse leaves the browser window (top edge)
        const onMouseLeave = (e) => {
            if (e.clientY <= 0) trigger();
        };
        if (!isTouch) {
            document.addEventListener("mouseleave", onMouseLeave);
        }

        // Mobile: 45s OR 60% scroll — whichever comes first
        let onScroll;
        if (isTouch) {
            timerRef.current = setTimeout(() => trigger(), 45000);
            onScroll = () => {
                if (scrollFiredRef.current) return;
                const doc = document.documentElement;
                const total = doc.scrollHeight - window.innerHeight;
                if (total <= 0) return;
                const pct = window.scrollY / total;
                if (pct >= 0.6) {
                    scrollFiredRef.current = true;
                    trigger();
                }
            };
            window.addEventListener("scroll", onScroll, { passive: true });
        }

        return () => {
            document.removeEventListener("mouseleave", onMouseLeave);
            if (onScroll) window.removeEventListener("scroll", onScroll);
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    // ESC + body scroll lock while open
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        window.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [open]);

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-3 sm:p-6 bg-black/50 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="exit-offer-title"
            data-testid="exit-intent-modal"
        >
            <div
                className="relative w-full max-w-lg bg-white rounded-2xl border border-[var(--border)] shadow-[0_30px_80px_-20px_rgba(15,23,42,0.4)] p-6 sm:p-8 rise-in overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Ambient accent glow */}
                <div
                    className="absolute -top-24 -right-16 w-72 h-72 rounded-full pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(circle, rgba(255,140,0,0.18), transparent 65%)",
                    }}
                    aria-hidden
                />

                <button
                    onClick={() => setOpen(false)}
                    aria-label="Close"
                    className="absolute top-3 right-3 w-9 h-9 rounded-full border border-[var(--border)] text-slate-500 hover:text-slate-900 hover:border-orange-400 transition-colors bg-white z-10"
                    data-testid="exit-intent-close"
                >
                    ✕
                </button>

                <div className="relative">
                    <div className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full border border-orange-400/60 bg-orange-50 text-orange-700">
                        <span
                            className="pulse-dot"
                            style={{ width: 6, height: 6, background: "#f97316" }}
                        />
                        Live Batch Filling Fast
                    </div>

                    <h3
                        id="exit-offer-title"
                        className="mt-4 font-display text-2xl sm:text-3xl font-extrabold text-slate-900 leading-tight tracking-tight"
                    >
                        🎯 Don't Miss This Live Batch
                    </h3>
                    <p className="mt-3 text-sm sm:text-base text-dim leading-relaxed">
                        The next Networking Workshop starts on{" "}
                        <span className="text-slate-900 font-semibold">
                            {batches.batch_date}
                        </span>
                        . Join hundreds of students learning enterprise networking through
                        live practical labs.
                    </p>

                    <ul
                        className="mt-5 space-y-2.5"
                        data-testid="exit-intent-trust-points"
                    >
                        {TRUST_POINTS.map((t) => (
                            <li
                                key={t}
                                className="flex items-center gap-3 text-[15px] text-slate-900"
                            >
                                <span className="w-5 h-5 shrink-0 rounded-full bg-[rgba(124,231,59,0.15)] border border-[rgba(124,231,59,0.5)] text-brand flex items-center justify-center">
                                    <svg
                                        width="12"
                                        height="12"
                                        viewBox="0 0 24 24"
                                        aria-hidden
                                    >
                                        <path
                                            fill="currentColor"
                                            d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"
                                        />
                                    </svg>
                                </span>
                                <span>{t}</span>
                            </li>
                        ))}
                    </ul>

                    <a
                        href={settings.razorpay_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setOpen(false)}
                        className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl px-6 py-4 text-lg font-bold text-white tracking-tight transition-all"
                        style={{
                            background:
                                "linear-gradient(180deg, #ff8f2b 0%, #f4691a 100%)",
                            boxShadow:
                                "0 0 0 1px rgba(196,74,10,0.35), 0 14px 30px -8px rgba(244,105,26,0.55), inset 0 -2px 0 rgba(0,0,0,0.12)",
                        }}
                        data-testid="exit-intent-cta"
                    >
                        <span>🚀 Reserve My Seat – ₹799</span>
                    </a>

                    <div className="mt-3 text-center text-[11px] font-mono text-mute tracking-widest uppercase">
                        Secure Razorpay checkout · UPI · Cards · Wallets
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExitIntent;
